import React, { useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Success from "../message/Success";
import Error from "../message/Error";
import fullGrayStar from "../../assets/images/fullGrayStar.svg";
import fullYellowStar from "../../assets/images/fullYellowStar.svg";
import whiteCart from "../../assets/images/whiteCart.svg";
import greenCart from "../../assets/images/greenCart.svg";

const SearchProductCard = ({
  image,
  title,
  originalPrice,
  discountedPrice,
  vendor,
  vendorId,
  productId,
  rating,
  sponsored,
  topSelling,
  reviews,
  onAddToCart,
  onClickProductCard,
}) => {
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { info: customer } = useSelector((state) => state.frontendAuth);

  const discountPercentage =
    originalPrice && discountedPrice
      ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
      : null;

  const handleAddToCartClick = async (e) => {
    e.stopPropagation();

    if (!customer) {
      navigate("/login"); // ✅ Redirect guest users
      return;
    }
    setIsAddingToCart(true);
    try {
      await onAddToCart({
        productId,
        qty: 1,
        price: discountedPrice || originalPrice,
        vendorId,
        storeName: vendor,
      });
      setSuccessMessage("Product added to cart!");
    } catch (error) {
      setErrorMessage("Failed to add product to cart.");
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleVendorClick = (e) => {
    e.stopPropagation();
    navigate(`/vendors/${vendor}`);
  };

  const handleProductClick = () => {
    navigate(`/products/${productId}`);
  };

  return (
    <div
      className="relative rounded-lg shadow-md transition-shadow duration-300 hover:border-agBorder cursor-pointer"
      onClick={handleProductClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sponsored and Top Selling Badges */}
      <div className="absolute top-2 flex flex-col justify-left items-left space-y-2 mx-2">
        {sponsored && (
          <span className="bg-agRedBg text-peach-dark text-xs px-2 py-1 rounded-md">
            Sponsored
          </span>
        )}
        {topSelling && (
          <span className="bg-agPrimaryLight text-agBlack text-xs px-2 py-1 rounded-md">
            Top Selling
          </span>
        )}
      </div>

      {/* Product Image */}
      <div className="mb-4">
        <img
          src={image}
          alt={title}
          className="object-contain w-full h-36 rounded-t-md"
        />
      </div>

      {/* Product Title */}
      <h3 className="text-agBlack font-nunito text-sm truncate mx-2">
        {title}
      </h3>

      {/* Price Section */}
      <div className="flex space-x-4">
        <div className="mt-2 mx-2">
          {discountedPrice ? (
            // <div className="flex items-center space-x-2">
            <div className="flex flex-col items-left ">
              <span className="text-agBlack font-semibold text-lg">
                <span className="text-sm">₦</span>
                {discountedPrice.toLocaleString()}
              </span>
              <span className="text-agLightGray text-xs line-through">
                ₦{originalPrice.toLocaleString()}
              </span>
            </div>
          ) : (
            <span className="text-agPrimary font-semibold text-md">
              ₦{originalPrice.toLocaleString()}
            </span>
          )}
        </div>

        <div className="mt-2">
          {discountPercentage && (
            <div className="text-agLightGray text-xs bg-agBg py-1 px-2 rounded-lg mt-1 mx-2">
              -{discountPercentage}%
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-between my-2">
        <div>
          {/* Vendor Info */}
          <p className="text-xs text-agLightGray mt-2 mx-2">
            Sold by{" "}
            <span className="text-agBlue" onClick={handleVendorClick}>
              {vendor}
            </span>
          </p>

          {/* Rating Section */}
          <div className="flex items-center mt-2">
            {reviews > 0 ? (
              <>
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={
                      star <= Math.round(rating) ? fullYellowStar : fullGrayStar
                    }
                    alt={`${star} star`}
                    className="w-4 h-4"
                  />
                ))}
                <span className="text-xs text-agLightGray ml-1">
                  ({reviews})
                </span>
              </>
            ) : (
              <span className="text-xs text-agLightGray ml-2">
                No reviews yet
              </span>
            )}
          </div>
        </div>

        {/* Cart Button */}

        <div className="mx-2 my-2 flex justify-center items-center">
          <div
            className={`relative w-12 h-8 flex items-center justify-center rounded-full transition-all duration-700 ${
              isHovered
                ? "bg-agPrimary"
                : "bg-transparent border-agPrimary border-2"
            }`}
            onClick={handleAddToCartClick}
          >
            {isAddingToCart ? (
              <div className="absolute w-6 h-6 loader-search-product-card"></div>
            ) : isHovered ? (
              <img
                src={whiteCart}
                alt="Add to Cart"
                className="w-6 h-6 transition-opacity duration-700"
              />
            ) : (
              <img
                src={greenCart}
                alt="Add to Cart"
                className="w-6 h-6 transition-opacity duration-700"
              />
            )}
          </div>
        </div>
      </div>
      {/* Feedback Messages */}
      {successMessage && (
        <Success
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <Error message={errorMessage} onClose={() => setErrorMessage("")} />
      )}
    </div>
  );
};

SearchProductCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  originalPrice: PropTypes.number.isRequired,
  discountedPrice: PropTypes.number,
  vendor: PropTypes.string.isRequired,
  vendorId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  rating: PropTypes.number,
  reviews: PropTypes.number,
  sponsored: PropTypes.bool,
  topSelling: PropTypes.bool,
  onAddToCart: PropTypes.func.isRequired,
  onClickProductCard: PropTypes.func.isRequired,
};

export default SearchProductCard;
